import cx from 'classnames';
import React from 'react';
import './PreviewContent.scss';
import Icon, { IconName } from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import { chooseWhatToShow } from './chooseWhatToShow';
import Truncate from './Truncate';
import Link from 'components/Link';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import Audio from 'components/Audio';

const addUrlProtocolIfNeeded = (url: string) => {
  if (!/^https?:\/\//i.test(url)) {
    return 'http://' + url;
  }
  return url;
};
export interface Props {
  title?: string;
  subtitle?: string | null;
  domain?: string;
  url?: string;
  thumbnail?: string | null;
  previewContentType: 'image' | 'video' | 'pdf' | 'file' | 'link' | 'audio';
  isFileSizeTooLarge?: boolean;
  expand?: boolean;
  downloadable?: boolean;
  showTooltips?: boolean;
  visual:
    | 'hovered'
    | 'normal'
    | 'delete-hovered'
    | 'loading'
    | 'error'
    | 'error-hovered'
    | 'error-delete-hovered';
  onMouseEnter: () => void;
  onMouseOut: () => void;
  onMouseEnterDelete: () => void;
  onMouseOutDelete: () => void;
  onDeleteClicked?: () => void;
}

const PreviewContent = ({
  previewContentType,
  isFileSizeTooLarge,
  title,
  subtitle,
  domain,
  url,
  thumbnail,
  expand,
  downloadable,
  showTooltips,
  visual = 'normal',
  onMouseEnter,
  onMouseOut,
  onMouseEnterDelete,
  onMouseOutDelete,
  onDeleteClicked,
}: Props) => {
  const show = chooseWhatToShow(visual, !!thumbnail, previewContentType);
  let previewContent = (
    <div
      data-testid="attachment-preview"
      className={cx('Preview', { withRectangle: show.rectangle, expand: expand })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseOut}
    >
      <div
        className={cx('box', {
          withRectangle: show.rectangle,
          changedColor: show.changeRectangleBgColor,
        })}
      >
        {show.image && (
          <img
            className={cx('image', { pale: show.pale }, { withRectangle: show.rectangle })}
            src={thumbnail || undefined}
            alt="Upload Preview"
          />
        )}
        {show.icon && (
          <div className="videoIconContainer" data-testid={`icon-${show.icon}`}>
            <Icon name={show.icon as IconName} size="xxlarge" />
          </div>
        )}
        {show.delete && onDeleteClicked && (
          <div
            data-testid="delete-button"
            className={cx('delete', { hovered: show.changeDeleteColor })}
            onClick={(e) => {
              e.preventDefault();
              onDeleteClicked && onDeleteClicked();
            }}
            onMouseEnter={onMouseEnterDelete}
            onMouseLeave={onMouseOutDelete}
          >
            <Icon name="x" size="inherit" />
          </div>
        )}
        {show.loading && (
          <div className="loading">
            <LoadingIndicator height={40} size="small" />
          </div>
        )}
        {show.error && (
          <div className="error" data-testid="error">
            <Icon name="warning" size="large" />
          </div>
        )}
        {show.hoveredErrorMessage && (
          <div className="hoveredErrorMessage">
            Upload
            <br />
            failed
            {isFileSizeTooLarge && (
              <>
                :
                <br />
                File over
                <br />
                10 MB
              </>
            )}
          </div>
        )}
      </div>
      {show.rectangle && (
        <div
          className={cx('rectangle', {
            changedColor: show.changeRectangleBgColor,
            expand: expand,
          })}
        >
          <div className={cx('title', { link: show.link })}>
            <Truncate className={cx({ 'two-lines': show.twoLinedTitle })}>
              {title as string}
            </Truncate>
          </div>
          <div className={cx('subtitle', { link: show.link })}>
            <Truncate>{subtitle as string}</Truncate>
          </div>
          {domain && (
            <div className="domain">
              <Truncate>{domain as string}</Truncate>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (url && (previewContentType === 'link' || downloadable)) {
    previewContent = (
      <Link href={addUrlProtocolIfNeeded(url)} target="_blank">
        {previewContent}
      </Link>
    );
  }

  // If it was an audio we need to render a diff component
  if (url && previewContentType === 'audio') {
    previewContent = (
      <div
        data-testid="attachment-preview"
        className={cx('Preview', { withRectangle: show.rectangle, expand: expand })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseOut}
      >
        {show.delete && onDeleteClicked && (
          <div
            data-testid="delete-button"
            className={cx('delete', { hovered: show.changeDeleteColor })}
            onClick={(e) => {
              e.preventDefault();
              onDeleteClicked && onDeleteClicked();
            }}
            onMouseEnter={onMouseEnterDelete}
            onMouseLeave={onMouseOutDelete}
          >
            <Icon name="x" size="inherit" />
          </div>
        )}
        <Audio src={url} key={url} />
      </div>
    );
  }

  return title && showTooltips ? (
    <div className="PreviewContent-container">
      <TooltipTrigger
        placement="top"
        tooltip={<Tooltip className="previewTooltip">{title}</Tooltip>}
      >
        {previewContent}
      </TooltipTrigger>
    </div>
  ) : (
    previewContent
  );
};

export default PreviewContent;
